// Based upon `@dxos/invariant`.

/**
 * Metadata injected by the log transform plugin.
 * Field names are intentionally short to reduce the size of the generated code.
 */
interface CallMetadata {
  /**
   * File name.
   */
  F: string;

  /**
   * Line number.
   */
  L: number;

  /**
   * Value of `this` at the site of the log call.
   * Will be set to the class instance if the call is inside a method, or to the `globalThis` (`window` or `global`) otherwise.
   */
  S: unknown | undefined;

  /**
   * A callback that will invoke the provided function with provided arguments.
   * Useful in the browser to force a `console.log` call to have a certain stack-trace.
   */
  C?: (fn: Function, args: unknown[]) => void;

  /**
   * Source code of the argument list.
   */
  A?: string[];
}

class InvariantViolation extends Error {
  constructor(message: string) {
    super(message);
    // NOTE: Restores prototype chain (https://stackoverflow.com/a/48342359).
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

type InvariantFn = (condition: unknown, message?: string, meta?: CallMetadata) => asserts condition;

/**
 * Asserts that the condition is true.
 */
export const invariant: InvariantFn = (
  condition: unknown,
  message?: string,
  meta?: CallMetadata,
): asserts condition => {
  if (condition) {
    return;
  }

  if (message?.startsWith('BUG')) {
    // This invariant is a debug bug-check: break if the debugger is attached.
    debugger;
  }

  let errorMessage = 'invariant violation';

  if (message) {
    errorMessage += `: ${message}`;
  }

  if (meta?.A) {
    errorMessage += ` [${meta.A[0]}]`;
  }

  if (meta?.F) {
    errorMessage += ` at ${meta.F}:${meta.L}`;
  }

  const error = new InvariantViolation(errorMessage);

  // Do not include the invariant function in the stack trace.
  Error.captureStackTrace(error, invariant);

  throw error;
};
